import { useMemo } from 'react'

import { IntegrationServices } from '@sequencehq/api'
import { InputSelectControl } from '@sequencehq/core-components'

import { CustomerLedgerIntegration } from 'Invoices/hooks/useCustomerLedgerIntegrations'
import { FieldLabel } from '../layout'
import { isFieldInvalid } from '../validation'

type ExternalId = { key: IntegrationServices; value: string }

type Props = {
  customerLedgerIntegrations: CustomerLedgerIntegration[]
  externalIds: ExternalId[]
  setExternalIds: (next: ExternalId[]) => void
  disabled?: boolean
  validationResults: Array<{
    key: IntegrationServices
    value: string[]
  }>
}

export const LedgerIntegrationFields = ({
  customerLedgerIntegrations,
  externalIds,
  setExternalIds,
  disabled,
  validationResults
}: Props) => {
  return customerLedgerIntegrations.map(integration => {
    const value = externalIds.find(
      externalId => externalId.key === integration.id
    )?.value

    const integrationValidationResults = validationResults.find(
      result => result.key === integration.id
    )

    return (
      <FieldLabel
        key={integration.id}
        label={`${integration.id} ledger account`}
      >
        <LedgerIntegrationField
          options={integration.options}
          value={value}
          onChange={value => {
            setExternalIds(
              externalIds.map(externalId => {
                if (externalId.key !== integration.id) return externalId
                return { key: integration.id, value }
              })
            )
          }}
          disabled={disabled}
          isInvalid={isFieldInvalid(integrationValidationResults?.value)}
        />
      </FieldLabel>
    )
  })
}

type LedgerIntegrationFieldProps = {
  options: CustomerLedgerIntegration['options']
  value: string | undefined
  onChange: (code: string) => void
  disabled?: boolean
  isInvalid: boolean
}

const LedgerIntegrationField = ({
  options,
  value,
  onChange,
  disabled,
  isInvalid
}: LedgerIntegrationFieldProps) => {
  const controlOptions = useMemo(() => {
    return options.map(o => ({
      value: o.code,
      label: `${o.name} (${o.code})`
    }))
  }, [options])

  return (
    <InputSelectControl
      options={controlOptions}
      value={value}
      onChange={onChange}
      placeholder={!disabled ? 'Select ledger account' : undefined}
      disabled={disabled}
      isInvalid={isInvalid}
    />
  )
}
